/**
 * Focus Trapping Composable
 *
 * Provides utilities for trapping focus within a specific container,
 * commonly used for modal dialogs, popups, and other overlay components.
 * Ensures keyboard users cannot tab out of the container until it's closed.
 */

import { ref, onMounted, onUnmounted } from 'vue';
import { useFocusManagement, type FocusableElement } from './useFocusManagement';

export interface FocusTrappingOptions {
  /**
   * Whether to automatically focus the first focusable element when trapping starts
   */
  autoFocusFirst?: boolean;

  /**
   * Whether to automatically focus the last focusable element when trapping starts
   */
  autoFocusLast?: boolean;

  /**
   * Selector for the element to focus initially (overrides autoFocusFirst/Last)
   */
  initialFocusSelector?: string;

  /**
   * Whether to restore focus when trapping is disabled
   */
  restoreFocusOnDisable?: boolean;

  /**
   * Whether to prevent escape key from disabling the trap
   */
  preventEscapeKeyDisable?: boolean;

  /**
   * Callback when escape key is pressed
   */
  onEscapeKeyPressed?: () => void;

  /**
   * Callback when focus trap is activated
   */
  onActivate?: () => void;

  /**
   * Callback when focus trap is deactivated
   */
  onDeactivate?: () => void;
}

export const useFocusTrapping = (options: FocusTrappingOptions = {}) => {
  const {
    autoFocusFirst = true,
    autoFocusLast = false,
    initialFocusSelector,
    restoreFocusOnDisable = true,
    preventEscapeKeyDisable = false,
    onEscapeKeyPressed,
    onActivate,
    onDeactivate,
  } = options;

  const focusManagement = useFocusManagement({ restoreOnUnmount: false });

  // State
  const isActive = ref(false);
  const trapContainer = ref<HTMLElement | null>(null);
  const previouslyFocusedElement = ref<HTMLElement | null>(null);

  // Internal references
  let firstFocusableElement: HTMLElement | null = null;
  let lastFocusableElement: HTMLElement | null = null;

  /**
   * Get all focusable elements in the current container
   */
  const updateFocusableElements = () => {
    if (!trapContainer.value) {
      firstFocusableElement = null;
      lastFocusableElement = null;
      return;
    }

    const focusableElements = focusManagement.getFocusableElements(trapContainer.value);
    firstFocusableElement = (focusableElements[0] as FocusableElement) || null;
    lastFocusableElement =
      (focusableElements[focusableElements.length - 1] as FocusableElement) || null;
  };

  /**
   * Handle tab key navigation within the trap
   */
  const handleTabKey = (event: KeyboardEvent) => {
    if (!isActive.value || !trapContainer.value) return;

    updateFocusableElements();

    if (!firstFocusableElement || !lastFocusableElement) {
      // No focusable elements, prevent tabbing
      event.preventDefault();
      return;
    }

    const activeElement = document.activeElement as HTMLElement;
    const isShiftPressed = event.shiftKey;

    if (isShiftPressed) {
      // Shift + Tab (backward navigation)
      if (activeElement === firstFocusableElement) {
        event.preventDefault();
        lastFocusableElement.focus();
      }
    } else {
      // Tab (forward navigation)
      if (activeElement === lastFocusableElement) {
        event.preventDefault();
        firstFocusableElement.focus();
      }
    }
  };

  /**
   * Handle escape key press
   */
  const handleEscapeKey = (event: KeyboardEvent) => {
    if (!isActive.value) return;

    if (onEscapeKeyPressed) {
      onEscapeKeyPressed();
    }

    if (!preventEscapeKeyDisable) {
      event.preventDefault();
      disable();
    }
  };

  /**
   * Handle keyboard events for focus trapping
   */
  const handleKeyDown = (event: KeyboardEvent) => {
    if (!isActive.value) return;

    switch (event.key) {
      case 'Tab':
        handleTabKey(event);
        break;
      case 'Escape':
        handleEscapeKey(event);
        break;
    }
  };

  /**
   * Handle focus events to ensure focus stays within the container
   */
  const handleFocusIn = (event: FocusEvent) => {
    if (!isActive.value || !trapContainer.value) return;

    const target = event.target as HTMLElement;

    // Check if focus moved outside the container
    if (!trapContainer.value.contains(target)) {
      event.preventDefault();

      // Return focus to the container
      updateFocusableElements();
      if (firstFocusableElement) {
        firstFocusableElement.focus();
      } else if (trapContainer.value.hasAttribute('tabindex')) {
        trapContainer.value.focus();
      }
    }
  };

  /**
   * Enable focus trapping on the specified container
   */
  const enable = (container: HTMLElement | string) => {
    if (isActive.value) {
      disable();
    }

    // Get container element
    if (typeof container === 'string') {
      trapContainer.value = document.querySelector(container);
    } else {
      trapContainer.value = container;
    }

    if (!trapContainer.value) {
      console.warn('Focus trap container not found');
      return false;
    }

    // Save currently focused element
    previouslyFocusedElement.value = document.activeElement as HTMLElement;

    // Activate trap
    isActive.value = true;

    // Add event listeners
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('focusin', handleFocusIn, true);

    // Set initial focus
    updateFocusableElements();
    setInitialFocus();

    // Ensure container is focusable if no focusable children
    if (!firstFocusableElement && !trapContainer.value.hasAttribute('tabindex')) {
      trapContainer.value.setAttribute('tabindex', '-1');
    }

    onActivate?.();
    return true;
  };

  /**
   * Set initial focus based on options
   */
  const setInitialFocus = () => {
    if (!trapContainer.value) return;

    let elementToFocus: HTMLElement | null = null;

    // Try initial focus selector first
    if (initialFocusSelector) {
      elementToFocus = trapContainer.value.querySelector(initialFocusSelector);
    }

    // Fallback to first or last focusable element
    if (!elementToFocus) {
      if (autoFocusLast && lastFocusableElement) {
        elementToFocus = lastFocusableElement;
      } else if (autoFocusFirst && firstFocusableElement) {
        elementToFocus = firstFocusableElement;
      } else if (trapContainer.value.hasAttribute('tabindex')) {
        elementToFocus = trapContainer.value;
      }
    }

    // Set focus
    if (elementToFocus && typeof elementToFocus.focus === 'function') {
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        elementToFocus!.focus();
      }, 0);
    }
  };

  /**
   * Disable focus trapping
   */
  const disable = () => {
    if (!isActive.value) return;

    isActive.value = false;

    // Remove event listeners
    document.removeEventListener('keydown', handleKeyDown, true);
    document.removeEventListener('focusin', handleFocusIn, true);

    // Restore focus if enabled
    if (restoreFocusOnDisable && previouslyFocusedElement.value) {
      // Use setTimeout to ensure any other DOM changes are complete
      setTimeout(() => {
        if (previouslyFocusedElement.value && document.contains(previouslyFocusedElement.value)) {
          previouslyFocusedElement.value.focus();
        }
      }, 0);
    }

    // Reset references
    trapContainer.value = null;
    previouslyFocusedElement.value = null;
    firstFocusableElement = null;
    lastFocusableElement = null;

    onDeactivate?.();
  };

  /**
   * Update focusable elements (useful when container content changes)
   */
  const refresh = () => {
    if (isActive.value) {
      updateFocusableElements();
    }
  };

  /**
   * Check if focus trapping is currently active
   */
  const getIsActive = () => isActive.value;

  /**
   * Get current trap container
   */
  const getContainer = () => trapContainer.value;

  /**
   * Move focus to first focusable element in trap
   */
  const focusFirst = () => {
    if (isActive.value && firstFocusableElement) {
      firstFocusableElement.focus();
      return true;
    }
    return false;
  };

  /**
   * Move focus to last focusable element in trap
   */
  const focusLast = () => {
    if (isActive.value && lastFocusableElement) {
      lastFocusableElement.focus();
      return true;
    }
    return false;
  };

  // Cleanup on unmount
  onUnmounted(() => {
    disable();
  });

  return {
    // State
    isActive: getIsActive,
    container: getContainer,

    // Core methods
    enable,
    disable,
    refresh,

    // Focus control
    focusFirst,
    focusLast,

    // Utilities
    updateFocusableElements,
  };
};

export default useFocusTrapping;
